import React, { useEffect, useState, useMemo } from 'react';
import nbrApi from '../../../services/nbrApi';
import { convertDataToFloorStructure } from '../../../services/common.utils';
import MonitorDoorCardV2 from './MonitorDoorCardV2';
import { getFloorMaxBadgeRowCount, getFloorMaxBelowBlockHeightPx } from './monitorDoorCardModel';
import FullPageLoader from '../../components/common/FullPageLoader';
import ReservationProfile from '../ReservationProfile/ReservationProfile';
import {
    readStoredBadgeDisplay,
    getMonitorStandaloneBadgePresentation,
    getMonitorStandaloneBadgeShellStyle,
    getMonitorUnassignedBadgeOuterStyle,
    MONITOR_BADGE_CHECKIN_COLOR,
    MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
    MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR
} from '../../../services/monitorBadgeDisplay';
import {
    enrichMonitorRoomsWithAssignedReservations,
    isReservationUnassignedForMonitor
} from '../../../services/monitorReservationUtils';
import moment from 'moment';
import '../../../css/multi-property-monitor.css';

function MultiPropertyMonitor() {
    const [properties, setProperties] = useState([]);
    const [selectedProperties, setSelectedProperties] = useState([]);
    const [propertyRooms, setPropertyRooms] = useState({});
    const [propertyReservations, setPropertyReservations] = useState({});
    const [propertyRoomAssignments, setPropertyRoomAssignments] = useState({}); // Store room_assignment_array for each property
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState('tabs'); // 'tabs' or 'windows'
    const [activeTab, setActiveTab] = useState(0);
    const [fullscreenProperty, setFullscreenProperty] = useState(null);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
    const [windowLayout, setWindowLayout] = useState('grid'); // 'grid' or 'horizontal' or 'vertical'
    const [showReservationModal, setShowReservationModal] = useState(false);
    const [selectedConfirmationNumber, setSelectedConfirmationNumber] = useState(null);
    const [selectedAssignmentContext, setSelectedAssignmentContext] = useState(null);

    const badgeLogin = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('selectedLoginData')) || {};
        } catch {
            return {};
        }
    }, []);
    const badgeDisplayMode = readStoredBadgeDisplay(badgeLogin);

    // Fetch available properties
    const fetchProperties = async () => {
        try {
            const response = await nbrApi.post('/listproperties');
            const data = response.data;
            setProperties(data?.map(prop => ({
                ...prop,
                value: prop.pk_id,
                label: prop.property_name
            })) || []);
        } catch (error) {
            console.error('Error fetching properties:', error);
        }
    };

    // Fetch room status for a specific property
    const fetchPropertyRooms = async (propertyId, propertyName) => {
        try {
            const payload = { properties_id: propertyId };

            const res = await nbrApi.post('/getroomstatuses', payload);
            if (res?.data) {
                // Use rooms_array from response instead of root data
                const roomsData = res?.data?.rooms_array || [];
                const reservationsData = res?.data?.reservations_array || [];
                const convertedData = convertDataToFloorStructure(roomsData);
                const enrichedFromReservations = enrichMonitorRoomsWithAssignedReservations(
                    convertedData,
                    reservationsData,
                    moment().format('YYYY-MM-DD')
                );
                setPropertyRooms(prev => ({
                    ...prev,
                    [propertyId]: {
                        name: propertyName,
                        rooms: enrichedFromReservations,
                        lastUpdated: new Date()
                    }
                }));
                
                // Store reservations_array for unassigned reservations
                setPropertyReservations(prev => ({
                    ...prev,
                    [propertyId]: reservationsData
                }));
                
                // Store room_assignment_array for room recommendations (up to 5 per unassigned reservation)
                const roomAssignmentsData = res?.data?.room_assignment_array || [];
                setPropertyRoomAssignments(prev => ({
                    ...prev,
                    [propertyId]: roomAssignmentsData
                }));
            }
        } catch (error) {
            console.error(`Error fetching rooms for property ${propertyId}:`, error);
        }
    };

    // Fetch rooms for all selected properties
    const fetchAllPropertyRooms = async (isInitialCall = false) => {
        if (selectedProperties.length === 0) return;

        if (isInitialCall) {
            setLoading(true);
        }

        const promises = selectedProperties.map(prop =>
            fetchPropertyRooms(prop.value, prop.label)
        );

        try {
            await Promise.all(promises);
        } catch (error) {
            console.error('Error fetching property rooms:', error);
        } finally {
            if (isInitialCall) {
                setLoading(false);
            }
        }
    };

    // Handle property selection
    const handlePropertyToggle = (property) => {
        setSelectedProperties(prev => {
            const isSelected = prev.some(p => p.value === property.value);
            if (isSelected) {
                // Remove property
                const newSelected = prev.filter(p => p.value !== property.value);
                // Remove from propertyRooms
                setPropertyRooms(prevRooms => {
                    const newRooms = { ...prevRooms };
                    delete newRooms[property.value];
                    return newRooms;
                });
                // Remove from propertyReservations
                setPropertyReservations(prevReservations => {
                    const newReservations = { ...prevReservations };
                    delete newReservations[property.value];
                    return newReservations;
                });
                // Remove from propertyRoomAssignments
                setPropertyRoomAssignments(prevAssignments => {
                    const newAssignments = { ...prevAssignments };
                    delete newAssignments[property.value];
                    return newAssignments;
                });
                return newSelected;
            } else {
                // Add property
                return [...prev, property];
            }
        });
    };

    // Get unique status options across all selected properties
    const uniqueStatusOptions = useMemo(() => {
        const allStatuses = [];
        Object.values(propertyRooms).forEach(propertyData => {
            propertyData.rooms.forEach(floor => {
                floor.rooms.forEach(room => {
                    if (room.status_display_text && !allStatuses.includes(room.status_display_text)) {
                        allStatuses.push(room.status_display_text);
                    }
                });
            });
        });
        return allStatuses.sort();
    }, [propertyRooms]);

    useEffect(() => {
        if (selectedStatusFilter !== 'all' && !uniqueStatusOptions.includes(selectedStatusFilter)) {
            setSelectedStatusFilter('all');
        }
    }, [uniqueStatusOptions, selectedStatusFilter]);

    // Filter rooms based on selected status
    const getFilteredRooms = useMemo(() => {
        const filtered = {};
        Object.entries(propertyRooms).forEach(([propertyId, propertyData]) => {
            if (selectedStatusFilter === 'all') {
                filtered[propertyId] = propertyData.rooms;
            } else {
                filtered[propertyId] = propertyData.rooms.map(floor => ({
                    ...floor,
                    rooms: floor.rooms.filter(room => room.status_display_text === selectedStatusFilter)
                })).filter(floor => floor.rooms.length > 0);
            }
        });
        return filtered;
    }, [propertyRooms, selectedStatusFilter]);

    // Auto-refresh data
    useEffect(() => {
        let intervalId;

        const startRefreshing = () => {
            fetchAllPropertyRooms(true);

            if (!intervalId) {
                intervalId = setInterval(() => {
                    if (document.visibilityState === 'visible') {
                        fetchAllPropertyRooms();
                    }
                }, 5000);
            }
        };

        const stopRefreshing = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        if (selectedProperties.length > 0 && document.visibilityState === 'visible') {
            startRefreshing();
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && selectedProperties.length > 0) {
                startRefreshing();
            } else {
                stopRefreshing();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopRefreshing();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedProperties]);

    // Initialize properties on component mount
    useEffect(() => {
        fetchProperties();
    }, []);

    const handleConfirmationBadgeClick = (confirmationNumber, assignmentContext) => {
        setSelectedConfirmationNumber(confirmationNumber);
        setSelectedAssignmentContext(
            assignmentContext === 'assign' || assignmentContext === 'show' ? assignmentContext : null
        );
        setShowReservationModal(true);
    };

    // Helper function to render confirmation badge (matching MonitorCard format)
    const renderConfirmationBadge = (conf) => {
        const isUnassigned = isReservationUnassignedForMonitor(conf);
        const pres = getMonitorStandaloneBadgePresentation(conf, badgeDisplayMode, { isUnassigned });
        const key = pres.confirmationForClick || conf.confirmation_number || conf;
        const hoverHandlers = {
            onMouseEnter: (e) => {
                e.currentTarget.style.opacity = '0.8';
            },
            onMouseLeave: (e) => {
                e.currentTarget.style.opacity = '1';
            }
        };

        if (pres.kind === 'unassigned') {
            return (
                <div
                    key={key}
                    onClick={() =>
                        handleConfirmationBadgeClick(pres.confirmationForClick, 'assign')
                    }
                    className="text-center text-decoration-none"
                    style={getMonitorUnassignedBadgeOuterStyle()}
                    {...hoverHandlers}
                    title={pres.tooltip}
                >
                    <div
                        style={{
                            backgroundColor: MONITOR_BADGE_CHECKIN_COLOR,
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX,
                            lineHeight: 1.2,
                            padding: '2px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 3,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        <span
                            aria-hidden
                            style={{ fontSize: Math.max(7, MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX - 4), lineHeight: 1 }}
                        >
                            ▲
                        </span>
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {pres.display}
                        </span>
                    </div>
                    <div
                        style={{
                            backgroundColor: '#fff',
                            color: MONITOR_UNASSIGNED_BADGE_ROOM_TYPE_COLOR,
                            fontWeight: 'bold',
                            fontSize: MONITOR_DOOR_BADGE_ROW_FONT_SIZE_PX + 3,
                            lineHeight: 1.2,
                            padding: '5px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {pres.roomTypeCode}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={key}
                onClick={() =>
                    handleConfirmationBadgeClick(pres.confirmationForClick, isUnassigned ? 'assign' : 'show')
                }
                className="text-center text-decoration-none"
                style={getMonitorStandaloneBadgeShellStyle(pres)}
                {...hoverHandlers}
                title={pres.tooltip}
            >
                {pres.display}
            </div>
        );
    };

    // Render reservations column for a property
    const renderReservationsColumn = (propertyId) => {
        const reservations = propertyReservations[propertyId] || [];
        
        if (!reservations || reservations.length === 0) {
            return (
                <div 
                    className="flex-grow-0"
                    style={{ 
                        width: 130,
                        minHeight: '100px'
                    }}
                />
            );
        }

        return (
            <div 
                className="flex-grow-0 d-flex flex-column align-items-center"
                style={{ 
                    width: 130,
                    minHeight: '100px',
                    paddingTop: '1rem',
                    position: 'sticky',
                    top: 0,
                    alignSelf: 'flex-start'
                }}
            >
                {reservations.map((reservation, index) => {
                    // Handle both object format and string format
                    const conf = typeof reservation === 'object' 
                        ? reservation 
                        : { confirmation_number: reservation };
                    return renderConfirmationBadge(conf);
                })}
            </div>
        );
    };

    // Render individual property monitor
    const renderPropertyMonitor = (propertyId, propertyData, isFullscreen = false) => {
        const rooms = getFilteredRooms[propertyId] || [];
        const totalRooms = rooms.reduce((total, floor) => total + floor.rooms.length, 0);

        return (
            <div
                key={propertyId}
                className={`property-monitor ${isFullscreen ? 'fullscreen' : ''}`}
                style={isFullscreen ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 9999,
                    backgroundColor: 'white',
                    overflow: 'auto'
                } : {}}
            >
                {/* Property Header */}
                <div className="card property-header mb-3">
                    <div className="card-body p-0">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="mb-1 text-primary fw-bold">
                                    <i className="bi bi-building me-2"></i>
                                    {propertyData.name}
                                </h4>
                                <p className="mb-0 text-muted">
                                    {totalRooms} Rooms • Last Updated: {propertyData.lastUpdated.toLocaleTimeString()}
                                </p>
                            </div>
                            {isFullscreen && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setFullscreenProperty(null)}
                                >
                                    <i className="bi bi-fullscreen-exit me-2"></i>
                                    Exit Fullscreen
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Rooms Display */}
                {rooms.length === 0 ? (
                    <div className="card shadow-sm border-0">
                        <div className="card-body empty-state">
                            <i className="bi bi-info-circle"></i>
                            <h4>No Rooms Found</h4>
                            <p>
                                {selectedStatusFilter === 'all'
                                    ? 'No rooms available for this property.'
                                    : `No rooms with status "${selectedStatusFilter}" found.`
                                }
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className='d-lg-flex align-items-start gap-3'>
                        {/* Reservations Column - shown once to the left of all floors */}
                        {renderReservationsColumn(propertyId)}
                        <div className="flex-grow-1">
                            {rooms.sort((a, b) => b.floor - a.floor).map((floor) => (
                        <div
                            key={floor.floor}
                            className='d-lg-flex align-items-start justify-content-between gap-3 pt-3 mb-4'
                            style={{
                                boxShadow: "rgba(33, 35, 38, 0.1) 0px 10px 10px -10px"
                            }}
                        >
                            <h4 className="mb-0 floor-number">
                                {floor.floor}
                            </h4>
                            <div className="flex-grow-1 room-display">
                                {(() => {
                                    const badgeSlotRowCount = getFloorMaxBadgeRowCount(
                                        floor.rooms,
                                        badgeDisplayMode
                                    );
                                    const footerSlotHeightPx = getFloorMaxBelowBlockHeightPx(floor.rooms);
                                    return floor.rooms.map((room) => (
                                        <MonitorDoorCardV2
                                            data={room}
                                            key={room.roomNumber}
                                            roomNumber={room.roomNumber}
                                            status={room.status}
                                            statusColor={room.statusColor}
                                            isDnd={room.isDnd}
                                            show_icon={room.show_icon}
                                            icon_blink_interval={room.icon_blink_interval}
                                            isMonitor={true}
                                            onConfirmationClick={handleConfirmationBadgeClick}
                                            badgeDisplayMode={badgeDisplayMode}
                                            badgeSlotRowCount={badgeSlotRowCount}
                                            footerSlotHeightPx={footerSlotHeightPx}
                                        />
                                    ));
                                })()}
                            </div>
                        </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Render tabbed view
    const renderTabbedView = () => {
        if (selectedProperties.length === 0) {
            return (
                <div className="card shadow-sm border-0">
                    <div className="card-body empty-state">
                        <i className="bi bi-building"></i>
                        <h4>No Properties Selected</h4>
                        <p>Select properties from the list above to start monitoring.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="tabbed-view">
                {/* Tab Navigation */}
                <ul className="nav nav-tabs nav-fill justify-content-start">
                    {selectedProperties.map((property, index) => {
                        const propertyData = propertyRooms[property.value];
                        const roomCount = propertyData ?
                            getFilteredRooms[property.value]?.reduce((total, floor) => total + floor.rooms.length, 0) || 0 : 0;

                        return (
                            <li className="nav-item" key={property.value}>
                                <button
                                    className={`nav-link ${activeTab === index ? 'active' : ''}`}
                                    onClick={() => setActiveTab(index)}
                                >
                                    <i className="bi bi-building me-2"></i>
                                    {property.label}
                                    <span className="badge bg-secondary ms-2">{roomCount}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                    {selectedProperties.map((property, index) => {
                        if (activeTab !== index) return null;

                        const propertyData = propertyRooms[property.value];
                        if (!propertyData) {
                            return (
                                <div key={property.value} className="loading-spinner">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p>Loading {property.label}...</p>
                                </div>
                            );
                        }

                        return renderPropertyMonitor(property.value, propertyData);
                    })}
                </div>
            </div>
        );
    };

    // Render windowed view
    const renderWindowedView = () => {
        if (selectedProperties.length === 0) {
            return (
                <div className="card shadow-sm border-0">
                    <div className="card-body empty-state">
                        <i className="bi bi-grid-3x3-gap"></i>
                        <h4>No Properties Selected</h4>
                        <p>Select properties from the list above to start monitoring.</p>
                    </div>
                </div>
            );
        }

        const getGridClass = () => {
            const count = selectedProperties.length;
            if (count === 1) return 'col-12';
            if (count === 2) return 'col-6';
            if (count <= 4) return 'col-6';
            if (count <= 6) return 'col-4';
            return 'col-3';
        };

        return (
            <div className={`windowed-view row ${windowLayout === 'horizontal' ? 'flex-nowrap' : ''}`}>
                {selectedProperties.map((property) => {
                    const propertyData = propertyRooms[property.value];
                    if (!propertyData) return null;
                    return (
                        <div
                            key={property.value}
                            className={`${getGridClass()} mb-4`}
                            // style={{ minHeight: '600px' }}
                        >
                            <div className="card property-window max-h-100">
                                <div className="card-header text-white d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">
                                        <i className="bi bi-building me-2"></i>
                                        {property.label}
                                    </h5>
                                   {propertyData?.rooms?.length > 0 &&  <button
                                        className="btn btn-sm btn-outline-light"
                                        onClick={() => setFullscreenProperty(property.value)}
                                    >
                                        <i className="bi bi-fullscreen me-1"></i>
                                        Fullscreen
                                    </button>}
                                </div>
                                <div className="card-body p-0" style={{ overflow: 'auto', maxHeight: '70vh' }}>
                                    {renderPropertyMonitor(property.value, propertyData)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="multi-property-monitor">
            {/* Property Selection */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <h5 className="mb-3 text-primary fw-bold">
                                <i className="bi bi-check2-square me-2"></i>
                                Select Properties to Monitor
                            </h5>
                            <div className="row">
                                {properties.map((property) => {
                                    const isSelected = selectedProperties.some(p => p.value === property.value);
                                    return (
                                        <div key={property.value} className="col-md-3 mb-2">
                                            <div
                                                className={`card property-selection-card ${isSelected ? 'selected' : ''}`}
                                                onClick={() => handlePropertyToggle(property)}
                                            >
                                                <div className="card-body p-3">
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handlePropertyToggle(property)}
                                                        />
                                                        <label className="form-check-label fw-medium">
                                                            {property.label}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status Filter */}
            {selectedProperties.length > 0 && (
                <div className="row">
                    <div className="col-12">
                        <div className="card status-filter-card">
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-3">
                                        <label htmlFor="statusFilter" className="form-label fw-bold text-primary mb-2">
                                            <i className="bi bi-funnel me-2"></i>
                                            Filter by Status
                                        </label>
                                    </div>
                                    <div className="col-md-6 mb-3 md:mb-0">
                                        <select
                                            id="statusFilter"
                                            className="form-select form-select-lg"
                                            value={selectedStatusFilter}
                                            onChange={(e) => setSelectedStatusFilter(e.target.value)}
                                        >
                                            <option value="all" className="fw-bold">🔄 All Statuses</option>
                                            {selectedStatusFilter !== 'all' && !uniqueStatusOptions.includes(selectedStatusFilter) && (
                                                <option value={selectedStatusFilter}>{selectedStatusFilter}</option>
                                            )}
                                            {uniqueStatusOptions.map((status) => (
                                                <option key={status} value={status} className="fw-medium">
                                                    {status}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 text-end">
                                        <span className="badge bg-primary fs-6 px-3 py-2">
                                            {Object.values(getFilteredRooms).reduce((total, rooms) =>
                                                total + rooms.reduce((floorTotal, floor) => floorTotal + floor.rooms.length, 0), 0
                                            )} Total Rooms
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Controls */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-body">
                            <div className="row align-items-center">
                                {/* <div className="col-md-4">
                                    <h3 className="mb-0 text-primary fw-bold">
                                        <i className="bi bi-grid-3x3-gap me-2"></i>
                                        Multi-Property Monitor
                                    </h3>
                                </div> */}
                                <div className="col-md-5">
                                    <div className="btn-group w-100" role="group">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="viewMode"
                                            id="tabsView"
                                            value="tabs"
                                            checked={viewMode === 'tabs'}
                                            onChange={(e) => setViewMode(e.target.value)}
                                        />
                                        <label className="btn btn-outline-primary rounded-start-pill" htmlFor="tabsView">
                                            <i className="bi bi-tabs me-2"></i>
                                            Tabbed View
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="viewMode"
                                            id="windowsView"
                                            value="windows"
                                            checked={viewMode === 'windows'}
                                            onChange={(e) => setViewMode(e.target.value)}
                                        />
                                        <label className="btn btn-outline-primary rounded-end-pill" htmlFor="windowsView">
                                            <i className="bi bi-grid-3x3-gap me-2"></i>
                                            Windowed View
                                        </label>
                                    </div>
                                </div>
                                <div className="col-md-7 text-end">
                                    <span className="badge bg-success fs-6 px-3 py-2">
                                        {selectedProperties.length} Properties Selected
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            {loading ? (
                <FullPageLoader />
            ) : (
                <>
                    {viewMode === 'tabs' ? renderTabbedView() : renderWindowedView()}

                    {/* Fullscreen Property View */}
                    {fullscreenProperty && (
                        <div className="fullscreen-overlay">
                            {(() => {
                                const property = selectedProperties.find(p => p.value === fullscreenProperty);
                                const propertyData = propertyRooms[fullscreenProperty];
                                if (!property || !propertyData) return null;

                                return renderPropertyMonitor(fullscreenProperty, propertyData, true);
                            })()}
                        </div>
                    )}

                    {/* Reservation Profile Modal */}
                    {showReservationModal && (
                        <ReservationProfile
                            show={showReservationModal}
                            onClose={() => {
                                setShowReservationModal(false);
                                setSelectedConfirmationNumber(null);
                                setSelectedAssignmentContext(null);
                            }}
                            confirmationNumber={selectedConfirmationNumber}
                            assignmentContext={selectedAssignmentContext}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default MultiPropertyMonitor;
